/**
 * The track grid, driven entirely by the column model.
 *
 * Cells are looked up by column id rather than written out in a fixed order, so
 * reordering, hiding and adding columns needs no change here. That indirection is
 * the whole reason the grid can be user-arranged.
 */

import { memo, useEffect, useMemo, useRef, useState, type DragEvent as ReactDragEvent, type MouseEvent as ReactMouseEvent, type ReactNode, type RefObject } from "react";
import { Artwork } from "../shell/Artwork";
import { Icon } from "../shell/Icons";
import { ReadyCell } from "../studio/Readiness";
import { ConfidenceCell, EditableCell, InlineEdit, confidenceClass } from "../cells";
import { reasonLabel } from "../core/needsEdit";
import type { Track } from "../types";
import { musicalName } from "../format/keys";
import type { KeyNotation } from "../format/notation";
import { formatNumber } from "../format/round";
import { Chips } from "./Chips";
import { sortField, type ColumnDef } from "./columns";
import { HeaderCell } from "./HeaderCell";

function seconds(v: number): string {
  return `${Math.floor(v / 60)}:${String(Math.round(v % 60)).padStart(2, "0")}`;
}

/*
 * Which columns take an EditableCell below is the whole policy, and it is worth
 * saying why the rest do not. LUFS, true peak, length and path are either facts
 * about the file or measurements with exactly one right answer; letting a
 * curator type over them would not be a correction, it would make the grid
 * disagree with the disk.
 */

/** Open a song's menu. `columnId` is the column that was right-clicked, so the menu can offer to edit it. */
export type RowMenuOpener = (track: Track, anchor: { rect: DOMRect } | { x: number; y: number }, columnId?: string) => void;

function renderCell(
  column: ColumnDef,
  track: Track,
  index: number,
  onFilter: (term: string) => void,
  onEdit: (track: Track, field: string, value: string) => Promise<string | null>,
  notation: KeyNotation,
  playing: boolean,
  onMenu?: RowMenuOpener
): ReactNode {
  switch (column.id) {
    case "index":
      return playing ? (
        <span className="idx playing" aria-label="Playing">
          <Icon name="play" size={11} />
        </span>
      ) : (
        <span className="idx">{index + 1}</span>
      );
    case "title":
      return (
        <EditableCell
          value={track.title ?? ""}
          onCommit={(v) => onEdit(track, "title", v)}
        >
          <span className="title-cell">
            <Artwork trackId={track.id} seed={track.album || track.artist || track.title} size={26} rounded={4} />
            <span className="title" title={track.title}>{track.title}</span>
          </span>
        </EditableCell>
      );
    case "more":
      return onMenu ? (
        <button
          className="nv-iconbtn row-more"
          aria-label={`Options for ${track.title}`}
          title="More options"
          onClick={(e) => {
            e.stopPropagation();
            onMenu(track, { rect: e.currentTarget.getBoundingClientRect() });
          }}
        >
          <Icon name="more" />
        </button>
      ) : null;
    case "artist":
      // The primary artist and any featured ones are separate chips, so a
      // curator can pull up everything a guest appears on -- which the packed
      // string "DJ Kaywise ft. Tekno & Mayorkun" makes impossible.
      return (
        <EditableCell
          value={track.artist ?? ""}
          title="Double-click to edit. The chips stay clickable for filtering."
          onCommit={(v) => onEdit(track, "artist", v)}
        >
          <Chips
            value={track.artist}
            parts={track.artists}
            onPick={(v) => onFilter(`artist:"${v}"`)}
            max={3}
          />
        </EditableCell>
      );
    case "album":
      return (
        <EditableCell value={track.album ?? ""} onCommit={(v) => onEdit(track, "album", v)}>
          {track.album ?? <span className="dim">—</span>}
        </EditableCell>
      );
    case "year":
      return (
        <EditableCell value={String(track.year ?? "")} onCommit={(v) => onEdit(track, "year", v)}>
          {track.year ?? <span className="dim">—</span>}
        </EditableCell>
      );
    case "genre":
      // Genre and sub-genre are separate tag fields but read as one idea, so
      // they share a cell. The precise class is styled apart from the broad one.
      return (
        <EditableCell
          value={track.genre ?? ""}
          title="Double-click to edit the genre tag. Chips stay clickable for filtering."
          onCommit={(v) => onEdit(track, "genre", v)}
        >
        <span className="genre-cell">
          <Chips
            value={track.genre}
            parts={track.genres}
            onPick={(v) => onFilter(`genre:"${v}"`)}
            max={3}
          />
          {track.subGenre && (
            <Chips
              value={track.subGenre}
              parts={track.subGenres}
              className="precise"
              onPick={(v) => onFilter(`subgenre:"${v}"`)}
              max={3}
            />
          )}
        </span>
        </EditableCell>
      );
    case "comment":
      // Stations routinely use the comment field as an ad-hoc key/value store,
      // so it gets the same chip treatment as genre.
      return (
        <EditableCell
          value={track.comment ?? ""}
          onCommit={(v) => onEdit(track, "comment", v)}
        >
          <Chips
            value={track.comment ?? null}
            parts={track.commentParts}
            onPick={(v) => onFilter(`comment:"${v}"`)}
            max={3}
          />
        </EditableCell>
      );
    case "bpm":
      // Clickable like the other chips: a curator building a set wants every
      // track near this tempo, and typing the number is friction for something
      // they are already looking at.
      return (
        <EditableCell
          value={track.bpm === null ? "" : formatNumber(track.bpm, 1)}
          placeholder="BPM"
          title={
            track.bpmSource === "curator"
              ? "Set by a curator. Double-click to change."
              : "Double-click to correct."
          }
          onCommit={(v) => onEdit(track, "bpm", v)}
        >
        <ConfidenceCell
          onPick={(v) => onFilter(`bpm:${formatNumber(Number(v), 1)}`)}
          value={track.bpm}
          confidence={track.bpmConfidence}
          format={(v) => formatNumber(Number(v), 1)}
          title={
            track.bpmSource === "curator"
              ? `Set by a curator. Nova detected ${
                  track.detectedBpm !== null && track.detectedBpm !== undefined
                    ? formatNumber(track.detectedBpm, 1)
                    : "nothing"
                }.`
              : undefined
          }
        />
        </EditableCell>
      );
    case "key":
      // Key is rarely a single certain answer on real material, so the runners-up
      // are shown alongside it rather than hidden in the review screen. A curator
      // deciding a harmonic transition needs to know the second candidate scored
      // 68% against the winner's 74%.
      return (
        <EditableCell
          value={
            (notation === "musical"
              ? track.musicalKeyName ?? track.musicalKey
              : track.musicalKey) ?? ""
          }
          placeholder="e.g. F minor or 4A"
          title={
            track.keySource === "curator"
              ? "Set by a curator. Double-click to change."
              : "Double-click to correct. Camelot or note names both work."
          }
          onCommit={(v) => onEdit(track, "key", v)}
        >
          <KeyCell track={track} onFilter={onFilter} notation={notation} />
        </EditableCell>
      );
    case "energy":
      return (
        <ConfidenceCell
          value={track.energy}
          confidence={track.energyConfidence}
          width="w-sm"
          format={(v) => Number(v).toFixed(2)}
          // A band around the value: two records at 0.53 and 0.55 feel the same.
          onPick={(v) => onFilter(`energy:${band(Number(v), 0.05)}`)}
        />
      );
    case "level": {
      const level = track.energyLevel ?? null;
      if (level === null) return <span className="skeleton w-sm" />;
      return (
        <button
          className="energy-bars clickable"
          title={`${
            track.energyLevelSource === "curator"
              ? `Energy ${level} of 5, set by hand${track.detectedEnergyLevel ? `; Nova measured ${track.detectedEnergyLevel}` : ""}`
              : `Energy ${level} of 5, measured by Nova`
          }. Click to find songs at this energy.`}
          aria-label={`Energy ${level} of 5`}
          onClick={(e) => {
            e.stopPropagation();
            onFilter(`energylevel:${level}`);
          }}
        >
          {[1, 2, 3, 4, 5].map((n) => (
            <i key={n} className={n <= level ? "f" : ""} />
          ))}
        </button>
      );
    }
    case "dance":
      return (
        <ConfidenceCell
          value={track.danceability ?? null}
          confidence={track.danceabilityConfidence ?? null}
          width="w-sm"
          format={(v) => Number(v).toFixed(2)}
          onPick={(v) => onFilter(`danceability:${band(Number(v), 0.05)}`)}
        />
      );
    case "lufs":
      return (
        <ConfidenceCell
          value={track.lufs}
          confidence={null}
          // Not snapped: LUFS is a calibrated measurement, and -8.05 is not -8.
          format={(v) => Number(v).toFixed(1)}
          // A 1 LU band: records a listener hears as equally loud.
          onPick={(v) => onFilter(`lufs:${(Number(v) - 0.5).toFixed(1)}..${(Number(v) + 0.5).toFixed(1)}`)}
        />
      );
    case "peak":
      return (
        <ConfidenceCell
          value={track.truePeak}
          confidence={null}
          width="w-sm"
          format={(v) => Number(v).toFixed(1)}
          onPick={(v) => onFilter(`truepeak:>=${Number(v).toFixed(1)}`)}
        />
      );
    case "cue":
      return track.cueIn !== null && track.cueOut !== null ? (
        <span
          className="val"
          title={`Audio runs ${track.cueIn.toFixed(3)}s to ${track.cueOut.toFixed(3)}s`}
        >
          <span className="conf-dot conf-exact" />
          {track.cueIn.toFixed(2)}–{track.cueOut.toFixed(1)}
        </span>
      ) : (
        <span className="skeleton w-lg" />
      );
    case "ready":
      return <ReadyCell track={track} />;
    case "duration":
      return <span className="dim tnum">{seconds(track.duration)}</span>;
    case "path":
      return <span className="dim path-cell">{track.path}</span>;
    case "needsedit":
      return track.needsEdit ? (
        <span className={`nv-flag ${track.needsEdit.reason === "dirty" ? "bad" : ""}`} title={[track.needsEdit.note, track.needsEdit.by && `Flagged by ${track.needsEdit.by}`].filter(Boolean).join(" · ") || undefined}>
          {reasonLabel(track.needsEdit.reason)}
        </span>
      ) : null;
    case "autopick": {
      if (track.manualOnly) {
        return (
          <span className="nv-flag" title={track.manualFolder && !track.ownManualOnly ? `Its folder “${track.manualFolder}” is hand-picked only` : "Auto DJ and Flow never pick it"}>
            Hand-picked
          </span>
        );
      }
      if (!track.availableFrom) return null;
      const later = new Date(track.availableFrom) > new Date();
      return later ? (
        <span className="nv-flag" title="Nothing plays it by itself before then">From {track.availableFrom.replace("T", " ")}</span>
      ) : (
        <span className="dim" title="Released">Out {track.availableFrom.slice(0, 10)}</span>
      );
    }
    default: {
      // Custom column, stored in the database.
      const value = track.custom?.[column.id];
      if (value === undefined || value === null || value === "") {
        return <span className="dim">—</span>;
      }
      if (column.valueType === "boolean") {
        return <span>{value === true || value === "true" ? "Yes" : "No"}</span>;
      }
      return <span>{String(value)}</span>;
    }
  }
}


/** Primary key plus its runners-up, because one key is usually an overstatement. */
function KeyCell({
  track,
  onFilter,
  notation,
}: {
  track: Track;
  onFilter: (term: string) => void;
  notation: KeyNotation;
}) {
  if (track.musicalKey === null) return <span className="skeleton w-sm" />;

  const ambiguous = track.musicalKey === "ambiguous";
  const camelot = track.musicalKey;
  const musical = track.musicalKeyName ?? musicalName(track.musicalKey);
  const shown = notation === "musical" ? musical : camelot;
  // The other spelling and the runners-up are still worth having, they are just
  // not worth a column. A curator weighing a transition can hover; one scanning
  // two hundred rows for the key of a record should see one value.
  const alternates = (track.keyCandidates ?? []).filter((c) => c.value !== camelot);
  const detail = [
    ambiguous ? null : notation === "musical" ? `Camelot ${camelot}` : musical,
    track.keyReason,
    alternates.length > 0
      ? "Also considered: " +
        alternates
          .map(
            (c) =>
              `${c.value} ${c.musical ?? musicalName(c.value)} (${Math.round(
                c.score * 100
              )}%)`
          )
          .join(", ")
      : null,
  ]
    .filter(Boolean)
    .join(" · ");

  return (
    <span className="val key-cell" title={detail || undefined}>
      <span className={`conf-dot ${confidenceClass(track.keyConfidence)}`} />
      {ambiguous ? (
        <span className="key-none">no key</span>
      ) : (
        <button
          className="key-badge clickable"
          title={`Filter to ${camelot} (${musical})`}
          onClick={(e) => {
            e.stopPropagation();
            // Filtering always goes by the Camelot code whatever is displayed:
            // the parser accepts both, and one canonical term keeps a search
            // built in one notation readable to somebody using the other.
            onFilter(`key:${camelot}`);
          }}
        >
          {shown}
        </button>
      )}
    </span>
  );
}


/**
 * Only the rows on screen are in the DOM.
 *
 * The grid used to render every loaded track, and at 427 of them that was 20,000
 * nodes, a quarter of a second per forced layout and 48 ms to process one scroll
 * step -- about twenty frames a second, which reads as the application
 * stuttering rather than as a long list. The library this is being built for is
 * a hundred thousand tracks, where the same code projects to four and a half
 * million nodes and would not run at all.
 *
 * So the table holds a window plus a small overscan, and two spacer rows stand
 * in for everything above and below it. The scrollbar stays honest because the
 * spacers carry the height the missing rows would have occupied, and scrolling
 * costs the same whether the library is four hundred tracks or a hundred
 * thousand.
 *
 * ROW_HEIGHT has to match what the CSS actually produces or the spacers drift
 * from the rows; it is measured from the first real row on mount rather than
 * hardcoded, so a font or padding change cannot silently break the arithmetic.
 */
const OVERSCAN = 10;
/** Rows past the visible ones to have loaded before they are scrolled to. */
const PREFETCH = 150;
const FALLBACK_ROW_HEIGHT = 28;

function useVisibleRange(
  total: number,
  scrollRef: RefObject<HTMLElement | null>,
  bodyRef: RefObject<HTMLTableSectionElement | null>
): { start: number; end: number; rowHeight: number } {
  const [rowHeight, setRowHeight] = useState(FALLBACK_ROW_HEIGHT);
  const [range, setRange] = useState({ start: 0, end: 60 });

  useEffect(() => {
    const container = scrollRef.current;
    if (!container) return;

    const measure = () => {
      const row = bodyRef.current?.querySelector("tr:not(.spacer)");
      const height = row instanceof HTMLElement ? row.offsetHeight : 0;
      if (height > 0 && Math.abs(height - rowHeight) > 0.5) setRowHeight(height);

      const unit = height > 0 ? height : rowHeight;
      const first = Math.max(0, Math.floor(container.scrollTop / unit) - OVERSCAN);
      const span = Math.ceil(container.clientHeight / unit) + OVERSCAN * 2;
      setRange((previous) => {
        const next = { start: first, end: Math.min(total, first + span) };
        return previous.start === next.start && previous.end === next.end
          ? previous
          : next;
      });
    };

    measure();
    container.addEventListener("scroll", measure, { passive: true });
    const observer = new ResizeObserver(measure);
    observer.observe(container);
    return () => {
      container.removeEventListener("scroll", measure);
      observer.disconnect();
    };
  }, [total, rowHeight, scrollRef, bodyRef]);

  return { ...range, rowHeight };
}

export function Grid({
  tracks,
  columns,
  selected,
  selectedIds,
  onSelect,
  onFilter,
  onEdit,
  onResize,
  onReorder,
  dragging,
  onDragState,
  scrollRef,
  notation,
  playingId = null,
  onOpen,
  onMenu,
  onHeaderMenu,
  inlineEdit = true,
  sort = null,
  onSort,
  rowCount,
  onNeedRows,
  onDragRows,
}: {
  tracks: Track[];
  columns: ColumnDef[];
  /** The row whose detail panel is open. */
  selected: string | null;
  /** Every selected row, for bulk actions. */
  selectedIds: Set<string>;
  onSelect: (id: string, mods: { shift: boolean; meta: boolean }) => void;
  onFilter: (term: string) => void;
  /** Commit an in-place correction. Returns an error to show, or null. */
  onEdit: (track: Track, field: string, value: string) => Promise<string | null>;
  onResize: (id: string, width: number) => void;
  onReorder: (fromId: string, toId: string) => void;
  dragging: string | null;
  onDragState: (id: string | null) => void;
  /** The scrolling ancestor, so only the rows inside it are rendered. */
  scrollRef: RefObject<HTMLElement | null>;
  /** How a key is spelled in this viewer's grid. */
  notation: KeyNotation;
  /** The track the player is on, marked in the # column. */
  playingId?: string | null;
  /** Double-click or Enter on a row: play from here. */
  onOpen?: (track: Track) => void;
  /** The row's ⋯ button, or a right-click. */
  onMenu?: RowMenuOpener;
  /** Double-click edits in place (tag editor) instead of playing (library). */
  inlineEdit?: boolean;
  /** Right-click on a column header. */
  onHeaderMenu?: (columnId: string, at: { x: number; y: number }) => void;
  /** The column the list is sorted by, if any. */
  sort?: { columnId: string; dir: "asc" | "desc" } | null;
  /** Click a header to sort by it (columns with nothing to sort by are left out). */
  onSort?: (columnId: string) => void;
  /** Every row there is, when only some are loaded (the scrollbar covers them all). */
  rowCount?: number;
  /** Asked when rows past the loaded ones come near: load up to this many. */
  onNeedRows?: (upTo: number) => void;
  /** Rows can be dragged out (to the Log, say): called as a drag starts on one. */
  onDragRows?: (track: Track, e: ReactDragEvent) => void;
}) {
  // Memoised so the array is the same object across scroll renders; a fresh
  // one each time would defeat the row memo below.
  const visible = useMemo(() => columns.filter((c) => !c.hidden), [columns]);
  const bodyRef = useRef<HTMLTableSectionElement | null>(null);
  // Sized for every row there is, loaded or not, so the scrollbar is the whole
  // list from the start; rows not loaded yet are asked for as they come near.
  const rows = Math.max(tracks.length, rowCount ?? 0);
  const { start, end, rowHeight } = useVisibleRange(rows, scrollRef, bodyRef);
  const drawnEnd = Math.min(end, tracks.length);
  const above = start * rowHeight;
  const below = Math.max(0, (rows - Math.max(start, drawnEnd)) * rowHeight);
  const waiting = Math.max(0, end - Math.max(start, drawnEnd));
  useEffect(() => {
    // A screen or two before the loaded rows run out.
    if (onNeedRows && rows > tracks.length && end + PREFETCH > tracks.length) onNeedRows(end + PREFETCH);
  }, [end, rows, tracks.length, onNeedRows]);

  return (
    <InlineEdit.Provider value={inlineEdit}>
    <table className="grid">
      <thead>
        <tr>
          {visible.map((col) => (
            <HeaderCell
              key={col.id}
              column={col}
              onResize={onResize}
              onReorder={onReorder}
              dragging={dragging}
              onDragState={onDragState}
              onMenu={onHeaderMenu}
              sorted={sort?.columnId === col.id ? sort.dir : null}
              onSort={onSort && sortField(col) ? onSort : undefined}
            />
          ))}
        </tr>
      </thead>
      <tbody ref={bodyRef}>
        {above > 0 && (
          <tr className="spacer" aria-hidden="true" style={{ height: above }} />
        )}
        {tracks.slice(start, drawnEnd).map((track, offset) => (
          <GridRow
            key={track.id}
            track={track}
            index={start + offset}
            visible={visible}
            isSelected={selectedIds.has(track.id)}
            isFocused={selected === track.id}
            onSelect={onSelect}
            onFilter={onFilter}
            onEdit={onEdit}
            notation={notation}
            playing={playingId === track.id}
            onOpen={onOpen}
            onMenu={onMenu}
            onDragRow={onDragRows}
          />
        ))}
        {/* Rows scrolled to before they have loaded: faint rows, drawn like the others, for the moment it takes. */}
        {Array.from({ length: waiting }, (_, i) => (
          <tr key={`loading-${i}`} className="loading" aria-hidden="true" style={{ height: rowHeight }}>
            <td colSpan={visible.length} />
          </tr>
        ))}
        {below - waiting * rowHeight > 0 && (
          <tr className="spacer" aria-hidden="true" style={{ height: below - waiting * rowHeight }} />
        )}
      </tbody>
    </table>
    </InlineEdit.Provider>
  );
}

/**
 * One grid row, memoised.
 *
 * Scrolling moves the rendered window a few rows at a time, but without this
 * every row in the window re-rendered on every step -- forty rows of fifteen
 * cells to show four new ones. Measured on 20,000 rows in a production build,
 * that was a 9.6 ms median step with 15% of steps over a 60 fps frame. A row
 * now renders only when its own track, position, selection or columns change.
 */
const GridRow = memo(function GridRow({
  track,
  index,
  visible,
  isSelected,
  isFocused,
  onSelect,
  onFilter,
  onEdit,
  notation,
  playing,
  onOpen,
  onMenu,
  onDragRow,
}: {
  track: Track;
  index: number;
  visible: ColumnDef[];
  isSelected: boolean;
  isFocused: boolean;
  onSelect: (id: string, mods: { shift: boolean; meta: boolean }) => void;
  onFilter: (term: string) => void;
  onEdit: (track: Track, field: string, value: string) => Promise<string | null>;
  notation: KeyNotation;
  playing: boolean;
  onOpen?: (track: Track) => void;
  onMenu?: RowMenuOpener;
  onDragRow?: (track: Track, e: ReactDragEvent) => void;
}) {
  return (
    <tr
      data-track-id={track.id}
      draggable={!!onDragRow}
      onDragStart={onDragRow ? (e) => onDragRow(track, e) : undefined}
      className={[isSelected ? "selected" : "", isFocused ? "focused" : "", playing ? "playing" : ""].join(" ")}
      aria-selected={isSelected}
      onClick={(e) => onSelect(track.id, { shift: e.shiftKey, meta: e.ctrlKey || e.metaKey })}
      onDoubleClick={(e) => {
        // A double-click inside an editable cell edits; elsewhere it plays.
        if ((e.target as HTMLElement).closest("input, .editing")) return;
        onOpen?.(track);
      }}
      onContextMenu={(e: ReactMouseEvent) => {
        if (!onMenu) return;
        e.preventDefault();
        if (!isSelected) onSelect(track.id, { shift: false, meta: false });
        const cell = (e.target as HTMLElement).closest("td");
        const column = cell ? /(?:^|\s)col-(\S+)/.exec(cell.className)?.[1] : undefined;
        onMenu(track, { x: e.clientX, y: e.clientY }, column);
      }}
    >
      {visible.map((col) => (
        <td
          key={col.id}
          className={`${col.align === "right" ? "num" : ""} col-${col.id}`}
          style={{ width: col.width, maxWidth: col.width }}
        >
          {renderCell(col, track, index, onFilter, onEdit, notation, playing, onMenu)}
        </td>
      ))}
    </tr>
  );
});

/** ``lo-hi`` around a 0-1 value, clipped, for a click-to-filter band. */
function band(value: number, half: number): string {
  const lo = Math.max(0, value - half);
  const hi = Math.min(1, value + half);
  return `${lo.toFixed(2)}-${hi.toFixed(2)}`;
}
